// components/abalyze/ext/FavoriteCard.jsx
import React, { useState, useMemo } from 'react';
import { FileSearchOutlined, StarOutlined, StarFilled } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { addFavorite, removeFavorite } from '../../../store/tz/reducer';
import { v4 as uuidv4 } from 'uuid';
import { getDocumentStatusDisplay } from '../../../utils/helpers';
import { useTheme } from '../../../theme/ThemeProvider';
import { getScoreCardWrapperStyle, getScoreCardInnerStyle } from '../../../theme/scoreBorder';

/**
 * Компонент карточки для избранного.
 * Отображает небольшую картинку слева и информацию (название, страна, статус) справа.
 *
 * @param {Object} props - Пропсы компонента.
 * @param {Object|null} props.document - Объект документа.
 * @param {string|null} props.imgSrc - Источник изображения.
 * @param {Function} props.onClick - Обработчик клика по карточке.
 * @param {number} props.score - Значение score для градиента.
 * @param {string} [props.searchQuery] - Поисковой запрос.
 * @param {string} [props.request_type] - Тип запроса.
 * @param {boolean} [props.compact] - Узкая карточка для таблицы избранного на маленьких экранах.
 * @returns {JSX.Element}
 */
const FavoriteCard = ({
  document,
  imgSrc,
  onClick,
  score,
  searchQuery = '',
  request_type = '',
  compact = false,
}) => {
  const { darkMode } = useTheme();
  // Состояние для отслеживания наведения курсора
  const [isHovered, setIsHovered] = useState(false);

  const dims = useMemo(
    () =>
      compact
        ? {
            w: 200,
            h: 100,
            img: 40,
            titleW: 130,
            titleFont: 12,
            smallFont: 11,
          }
        : {
            w: 250,
            h: 120,
            img: 50,
            titleW: 170,
            titleFont: 14,
            smallFont: 12,
          },
    [compact],
  );

  // Стили для внешнего контейнера (с рамкой по score)
  const containerStyle = getScoreCardWrapperStyle(score, darkMode, {
    width: dims.w,
    height: dims.h,
    boxSizing: 'border-box',
    cursor: 'pointer',
    overflow: 'hidden',
  });

  // Стили для внутреннего контейнера
  const cardInnerStyle = getScoreCardInnerStyle(darkMode, {
    display: 'flex',
    alignItems: 'center',
    borderRadius: '6px',
    padding: compact ? '6px' : '8px',
    transition: 'opacity 0.6s ease',
    opacity: isHovered ? 0 : 1,
    height: '100%',
  });

  // Стили для изображения (миниатюра)
  const imageStyle = {
    width: dims.img,
    height: dims.img,
    objectFit: 'contain', // сохраняем пропорции
    borderRadius: '4px',
    transition: 'opacity 0.6s ease', // замедленная анимация
  };

  // Стили для фолбека, если изображения нет
  const fallbackStyle = {
    width: dims.img,
    height: dims.img,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--theme-border)',
    borderRadius: '4px',
  };

  // Стили для информационного блока
  const infoStyle = {
    marginLeft: '8px',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  };

  // Стили для заголовка: вывод в одну строку с троеточием, если не помещается
  const titleStyle = {
    fontWeight: 'bold',
    margin: 0,
    fontSize: dims.titleFont,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: dims.titleW,
    maxWidth: '100%',
    display: 'block',
  };

  // Стили для дополнительного текста
  const textStyle = {
    margin: '2px 0',
    fontSize: dims.smallFont,
  };

  // Стили для строки статуса: ограничиваем по количеству строк
  const statusTextStyle = {
    margin: '2px 0',
    fontSize: dims.smallFont,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  };

  // Стили для увеличенного изображения при наведении
  const hoverImageStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'contain', // сохраняем пропорции изображения
    borderRadius: '8px',
    opacity: isHovered ? 1 : 0,
    transition: 'opacity 0.6s ease', // замедленная анимация
    zIndex: 2,
    pointerEvents: 'none', // чтобы не блокировать клики по карточке
  };

  const dispatch = useDispatch();
  const cardId = useMemo(() => {
    if (document?.['111_numReg'])          return '' + document['111_numReg'];
    if (document?.['210_Номер_заявки'])    return ''+ document['210_Номер_заявки'];
    if (document?.['Reg. No'])             return ''+ document['Reg. No'];
    if (document?.['@INTREGN'])            return ''+ document['@INTREGN'];
    const newId = uuidv4();
    if (document) document['111_numReg'] = newId; // сохраняем, чтобы не менялся
    return newId;
  }, [document]);
  // Получаем список избранного из стора
  const favorites = useSelector((state) => state.tz.favorites);
  const isFavorite = favorites.some((fav) => fav.id === cardId);

  // Обработчик клика по звёздочке — предотвращает всплытие события
  const toggleFavorite = (e) => {
    e.stopPropagation();
    if (isFavorite) {
      dispatch(removeFavorite(cardId));
    } else {
      dispatch(addFavorite({ id: cardId, searchQuery, document, imgSrc, score, request_type }));
    }
  };

  const statusLine = getDocumentStatusDisplay(document) || '—';

  // Расширенные поля для заголовка и страны (поддержка WIPO)
  const title = document?.realName
    || document?.Trademark
    || document?.['MARKGR/CURRENT/IMAGE@TEXT']
    || 'Без названия';
  const countryValue = document?.['190_country']
    || document?.['190_Страна']
    || document?.Origin
    || document?.['Holder country']
    || '—';


  return (
    <div
      style={containerStyle}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Абсолютное изображение, которое появляется при наведении */}
      {imgSrc && (
        <img
          src={imgSrc}
          alt={document?.realName || 'Без названия'}
          style={hoverImageStyle}
        />
      )}
      <div style={cardInnerStyle}>
        {imgSrc ? (
          <img src={imgSrc} alt={document?.realName || 'Без названия'} style={imageStyle} />
        ) : (
          <div style={fallbackStyle}>
            <FileSearchOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
          </div>
        )}
        <div style={infoStyle}>
          <p style={titleStyle}>{title}</p>
          <p style={textStyle}>
            <strong>Страна:</strong> {countryValue}
          </p>
          <p style={statusTextStyle}>
            <strong>Статус:</strong> {statusLine}
          </p>
        </div>
      </div>
      {/* Иконка избранного */}
      <div
        style={{
          position: 'absolute',
          top: '4px',
          right: '4px',
          zIndex: 3, // выше увеличенной картинки
          cursor: 'pointer',
        }}
        onClick={toggleFavorite}
      >
        {isFavorite ? (
          <StarFilled style={{ color: '#fadb14', fontSize: '18px' }} />
        ) : (
          <StarOutlined style={{ color: '#fadb14', fontSize: '18px' }} />
        )}
      </div>
    </div>
  );
};

export default React.memo(FavoriteCard);
